import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = ['#0F4C81', '#E8C547', '#2E86AB', '#A23B72', '#F18F01', '#44BBA4'];

function StatCard({ label, value, sub, accent, onClick, clickable }) {
  return (
    <div
      className={`stat-card ${accent ? 'stat-card--accent' : ''} ${clickable ? 'stat-card--clickable' : ''}`}
      onClick={onClick}
      style={clickable ? { cursor: 'pointer' } : {}}
    >
      <div className="stat-value">{value ?? '\u2014'}</div>
      <div className="stat-label">{sub || label}</div>
    </div>
  );
}

// ...existing code...

// Redirect to login page if DevTools or View Source shortcuts are pressed
function useDevToolsBlocker() {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F12
      if (e.key === 'F12') {
        window.location.href = '/login';
        e.preventDefault();
      }
      // Ctrl+Shift+I or Cmd+Opt+I
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
        window.location.href = '/login';
        e.preventDefault();
      }
      // Ctrl+U or Cmd+U
      if ((e.ctrlKey || e.metaKey) && (e.key === 'U' || e.key === 'u')) {
        window.location.href = '/login';
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}


// ...existing code...

function ProgressBar({ label, value, max, sub }) {
  // Clamp percent between 0 and 100, handle division by zero
  let percent = 0;
  let percentDisplay = '0%';
  if (max > 0) {
    percent = Math.round((value / max) * 100);
    percentDisplay = `${percent}%`;
    if (!isFinite(percent)) percent = 0;
    if (percent < 0) percent = 0;
    if (percent > 100) percent = 100;
  }
  return (
    <div className="progress-item" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <span style={{ fontWeight: 600, color: '#2C3E50' }}>{label}</span>
        <span style={{ fontWeight: 600, color: '#34495E' }}>{value.toLocaleString('en-IN')} / {max.toLocaleString('en-IN')} ({percentDisplay})</span>
      </div>
      <div style={{ background: '#E0E7ED', borderRadius: '8px', height: '12px', overflow: 'hidden' }}>
        <div style={{ background: '#2E86AB', height: '100%', width: `${percent}%`, transition: 'width 0.5s ease-in-out' }} />
      </div>
      {sub && <div style={{ fontSize: '0.85rem', color: '#7F8C8D', marginTop: '0.25rem' }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  useDevToolsBlocker();
  const { can } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [mgtStatus, setMgtStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.dashboard.summary(),
      can('mgt7a') ? api.mgt7a.status() : Promise.resolve([]),
    ])
      .then(([s, m]) => {
        setSummary(s);
        setMgtStatus(m);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const fmt = n => (n == null ? '—' : Number(n).toLocaleString('en-IN'));

  const pieData = mgtStatus.map(r => ({
    name: r.INC_20A_STATUS || 'Unknown',
    value: Number(r.CNT),
  }));

  if (loading) return <div className="page-loading"><div className="spinner" /><span>Loading dashboard…</span></div>;
  if (error) return <div className="page-error">Error: {error}</div>;

  // Disable right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  return (
    <div className="page" onContextMenu={handleContextMenu}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Overview</h1>
          <p className="page-desc">Real-time snapshot of the Glazing Genius data pipeline</p>
        </div>
        <div className="page-badge">Live</div>
      </div>

      {/* ── Top KPIs ─────────────────────────────────────── */}
      <section className="section">
        <h2 className="section-title">Companies</h2>
        <div className="stat-grid">
          <StatCard label="Total Companies" value={fmt(summary?.TOTAL_COMPANIES)} sub="Total companies in database" />
          <StatCard label="Master Records" value={fmt(summary?.TOTAL_MASTER_RECORDS)} sub="Master company records" />
          <StatCard label="MGT-7A Records" value={fmt(summary?.TOTAL_MGT7A)} sub="Companies with MGT-7A filings" />
          <StatCard label="MGT-7A Success" value={fmt(summary?.MGT7A_SUCCESS)} accent sub="Successful MGT-7A filings" />
          <StatCard label="INC-20A Required" value={fmt(summary?.INC20A_REQUIRED)} sub="INC-20A Required" />
          <StatCard label="Retry Needed" value={fmt(summary?.RETRY_NEEDED)} sub="Retry Needed" />
        </div>
      </section>

      {/* ── GST KPIs ─────────────────────────────────────── */}
      {can('gst') && (
        <section className="section">
          <h2 className="section-title">GST Enrichment</h2>
          <div className="stat-grid">
            <StatCard
              label="PANs with GSTIN"
              value={fmt(summary?.PAN_WITH_GST)}
              accent
              clickable
              onClick={() => nav('/gst')}
              sub="Unique PANs having at least 1 GSTIN"
            />
            <StatCard
              label="PANs without GSTIN"
              value={fmt(summary?.PAN_NO_GST)}
              sub="Success PANs with no GST record"
              clickable
              onClick={() => nav('/gst')}
            />
            <StatCard
              label="Total GSTIN Records"
              value={fmt(summary?.TOTAL_GST_RECORDS)}
              sub="Including multiple GSTINs per PAN"
              clickable
              onClick={() => nav('/gst')}
            />
          </div>
        </section>
      )}

      {/* ── IEC KPIs ─────────────────────────────────────── */}
      {can('iec') && (
        <section className="section">
          <h2 className="section-title">IEC / Import-Export (SIMS)</h2>
          <div className="stat-grid" style={{ gridTemplateColumns: 'minmax(250px, 400px)' }}>
            <StatCard
              label="IEC Companies"
              value={fmt(summary?.TOTAL_IEC_COMPANIES)}
              accent
              clickable
              onClick={() => nav('/iec')}
              sub="IEC Companies"
            />
          </div>
        </section>
      )}

      {/* ── Progress Tracking ────────────────────────────── */}
      {/* ── Progress Tracking ────────────────────────────── */}
      <section className="section" style={{ background: '#F8FAFC', padding: '1.5rem', borderRadius: '12px', border: '1px solid #E2E8F0', marginTop: '2rem' }}>
        <h2 className="section-title" style={{ marginBottom: '1.5rem', color: '#1E293B' }}>Progress Tracking</h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#475569', borderBottom: '1px solid #CBD5E1', paddingBottom: '0.5rem' }}>Campaign Progress</h3>
            
            <ProgressBar 
              label="Total Campaigns Overall" 
              value={summary?.SUCCESSFUL_CAMPAIGNS || 0} 
              max={summary?.TOTAL_CAMPAIGNS || 0} 
              sub="Overall success across all internal database campaigns" 
            />
            
            <ProgressBar 
              label="INC-20A Notice" 
              value={summary?.SUCCESS_INC20A || 0} 
              max={summary?.TARGET_INC20A || 0} 
            />
            
            <ProgressBar 
              label="GSTIN Pending" 
              value={summary?.SUCCESS_GSTIN_PENDING || 0} 
              max={summary?.TARGET_GSTIN_PENDING || 0} 
            />
            
            <ProgressBar 
              label="Trademark Promo" 
              value={summary?.SUCCESS_TRADEMARK || 0} 
              max={summary?.TARGET_TRADEMARK || 0} 
            />
            
            <ProgressBar 
              label="GST Registration Campaign" 
              value={summary?.SUCCESS_GST_REG || 0} 
              max={summary?.TARGET_GST_REG || 0} 
              sub="Targeting all master DB companies" 
            />
            
            <ProgressBar 
              label="All Export Services (SIMS)" 
              value={summary?.SUCCESS_SIMS || 0} 
              max={summary?.TARGET_SIMS || 0} 
            />
            
          </div>
        </div>
      </section>

      {/* ── MGT-7A Pie ────────────────────────────────────── */}
      {can('mgt7a') && pieData.length > 0 && (
        <section className="section">
          <h2 className="section-title">INC-20A / MGT-7A Status Breakdown</h2>
          <div className="chart-card">
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={130}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => v.toLocaleString('en-IN')} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-legend-table">
              {pieData.map((d, i) => (
                <div
                  key={d.name}
                  className="pie-legend-row"
                  onClick={() => nav(`/mgt7a?status=${encodeURIComponent(d.name)}`)}
                >
                  <span className="pie-dot" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="pie-name">{d.name}</span>
                  <span className="pie-count">{d.value.toLocaleString('en-IN')}</span>
                  <span className="pie-arrow">→</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
